import React, { useState, useEffect } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faArrowRight } from "@fortawesome/free-solid-svg-icons";
import { GET_FEATURED_PRODUCTS } from "../utils/constant";
import API_CLIENT from "../lib/api-client";
import Loading from "./loading";
import { Link } from "react-router-dom";
import { ProductCardSkeleton } from "./Skeleton";

const FeaturesProduct = () => {
  const [featuredProducts, setFeaturedProducts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchFeaturedProducts = async () => {
      try {
        setLoading(true);
        const response = await API_CLIENT.get(GET_FEATURED_PRODUCTS);
        if (response.status === 200) {
          setFeaturedProducts(response.data.featured.newArrivals);
          setLoading(false);
        }
      } catch (error) {
        console.error("Error fetching featured products:", error);
      }
    };
    fetchFeaturedProducts();
  }, []);

  if (loading) {
    return (
      <section className="py-10 bg-white w-full">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[...Array(3)].map((_, index) => (
              <FeaturedProductSkeleton key={index} delay={index * 150} />
            ))}
          </div>
          
          <div className="text-center mt-8">
            <div className="h-12 w-40 bg-gray-200 rounded-sm mx-auto animate-pulse relative overflow-hidden">
              <div className="absolute inset-0 -translate-x-full animate-shimmer bg-gradient-to-r from-transparent via-white/20 to-transparent" />
            </div>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section className="py-10 bg-white">
      <div className="container mx-auto px-4">

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {featuredProducts.slice(0, 6).map((product, index) => (
            <div 
              key={product._id} 
              className="bg-white rounded-lg shadow-sm hover:shadow-md overflow-hidden flex flex-col h-full transform transition-all duration-300 hover:-translate-y-2 group animate-fadeIn"
              style={{ animationDelay: `${index * 150}ms` }}
            >
              {/* Product Image */}
              <div className="relative h-56 overflow-hidden bg-neutral-100">
                <div className="absolute inset-0 bg-gradient-to-b from-transparent to-black/20 opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                <img 
                  src={product.images[0]} 
                  alt={product.name}
                  className="w-full h-full object-cover transform transition-transform duration-700 group-hover:scale-110"
                />
              </div>
              
              {/* Product Info - With improved styling */}
              <div className="p-5 flex flex-col flex-grow border-t border-neutral-100">
                <h3 className="text-lg font-medium text-neutral-800 mb-2 group-hover:text-teal-600 transition-colors duration-300">
                  {product.name}
                </h3>
                
                <div className="text-sm text-neutral-500 mb-3 inline-flex items-center">
                  <span className="inline-block h-2 w-2 rounded-full bg-teal-500 mr-2"></span>
                  {product.manufacturer}
                </div>
                
                <p className="text-neutral-600 text-sm flex-grow line-clamp-3 leading-relaxed">
                  {product.description}
                </p>
                
                <div className="flex justify-end mt-5 pt-4 border-t border-neutral-100">
                  <Link 
                    to={`/product/${product._id}`}
                    className="group/btn relative overflow-hidden w-10 h-10 bg-teal-600 text-white flex items-center justify-center rounded-full transition-all duration-300 shadow-sm hover:shadow-teal-200 hover:w-32"
                    aria-label={`View details for ${product.name}`}
                  >
                    <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover/btn:opacity-100 transition-opacity duration-300">
                      <span className="text-sm font-medium mr-2 whitespace-nowrap">View Details</span>
                      <FontAwesomeIcon icon={faArrowRight} />
                    </div>
                    
                    <FontAwesomeIcon 
                      icon={faArrowRight} 
                      className="opacity-100 group-hover/btn:opacity-0 transition-opacity duration-300"
                    />
                  </Link>
                </div>
              </div>
            </div>
          ))}
        </div>
        
        {/* View all products button - Unchanged as requested */}
        <div className="text-center mt-8">
          <Link 
            to="/products" 
            className="inline-block px-8 py-3 border border-neutral-300 hover:border-teal-500 text-neutral-700 hover:text-teal-600 rounded-sm transition-all duration-300"
          >
            View All Products
          </Link>
        </div>
      </div>
    </section>
  );
};

// Create a special skeleton for featured products that matches your design
const FeaturedProductSkeleton = ({ delay = 0 }) => (
  <div 
    className="bg-white rounded-lg shadow-sm overflow-hidden flex flex-col h-full animate-fadeIn"
    style={{ animationDelay: `${delay}ms` }}
  >
    {/* Product Image Skeleton */}
    <div className="relative h-56 bg-gray-200 animate-pulse overflow-hidden">
      <div className="absolute inset-0 -translate-x-full animate-shimmer bg-gradient-to-r from-transparent via-white/20 to-transparent" />
    </div>
    
    {/* Product Info Skeleton */}
    <div className="p-5 flex flex-col flex-grow border-t border-neutral-100">
      {/* Title */}
      <div className="h-7 w-3/4 bg-gray-200 rounded-md mb-2 animate-pulse relative overflow-hidden">
        <div className="absolute inset-0 -translate-x-full animate-shimmer bg-gradient-to-r from-transparent via-white/20 to-transparent" />
      </div>
      
      {/* Manufacturer */}
      <div className="flex items-center mb-3">
        <div className="h-2 w-2 rounded-full bg-gray-200 animate-pulse mr-2"></div>
        <div className="h-4 w-1/3 bg-gray-200 rounded-md animate-pulse relative overflow-hidden">
          <div className="absolute inset-0 -translate-x-full animate-shimmer bg-gradient-to-r from-transparent via-white/20 to-transparent" />
        </div>
      </div>
      
      {/* Description lines */}
      <div className="h-4 w-full bg-gray-200 rounded-md mb-2 animate-pulse relative overflow-hidden">
        <div className="absolute inset-0 -translate-x-full animate-shimmer bg-gradient-to-r from-transparent via-white/20 to-transparent" />
      </div>
      <div className="h-4 w-full bg-gray-200 rounded-md mb-2 animate-pulse relative overflow-hidden">
        <div className="absolute inset-0 -translate-x-full animate-shimmer bg-gradient-to-r from-transparent via-white/20 to-transparent" />
      </div>
      <div className="h-4 w-2/3 bg-gray-200 rounded-md flex-grow animate-pulse relative overflow-hidden">
        <div className="absolute inset-0 -translate-x-full animate-shimmer bg-gradient-to-r from-transparent via-white/20 to-transparent" />
      </div>
      
      {/* Button area */}
      <div className="flex justify-end mt-5 pt-4 border-t border-neutral-100">
        <div className="h-10 w-10 rounded-full bg-gray-200 animate-pulse relative overflow-hidden">
          <div className="absolute inset-0 -translate-x-full animate-shimmer bg-gradient-to-r from-transparent via-white/20 to-transparent" />
        </div>
      </div>
    </div>
  </div>
);

export default FeaturesProduct;
